/**
 * ChartInsightPanel Component
 *
 * Per-chart AI insight panel. Explains what the metric is, the current trend
 * analysis, key drivers, and which events/comments were factored in.
 * Opened via the AI button on each trend chart header.
 */

import React, { useEffect, useRef } from 'react';
import type { TrendInsight, MonthEvent } from '@/types/scalingUp';
import type { OlympicBest } from '@/types/scalingUp';
import './ChartInsightPanel.css';

// ---------------------------------------------------------------------------
// Metric definitions — what each metric IS and what a healthy target looks like
// ---------------------------------------------------------------------------
const METRIC_DEFINITIONS: Record<
  OlympicBest['metric'],
  { what: string; target: string; why: string }
> = {
  revenue: {
    what: 'Total gross revenue invoiced to clients in the month.',
    target: 'Growing 10–15% month-on-month, or tracking to annual budget.',
    why: 'The top line. Consistent revenue growth validates that the business is winning and retaining clients.',
  },
  grossMarginPercent: {
    what: 'Revenue minus direct costs (COGS), expressed as a percentage of revenue.',
    target: '≥50% for professional services. Below 40% signals pricing or delivery cost problems.',
    why: 'Shows how efficiently the business converts revenue into profit before overheads. Declining GM% often means scope creep, underpricing, or rising subcontractor costs.',
  },
  dler: {
    what: 'Direct Labour Efficiency Ratio — revenue generated per dollar of direct labour cost.',
    target: '≥3.0x is healthy. Below 2.5x means labour is not being leveraged effectively.',
    why: 'The core productivity metric for a professional services business. A falling DLER signals under-utilisation, over-staffing, or margin erosion on projects.',
  },
  cccDays: {
    what: 'Cash Conversion Cycle — average days from delivering work to receiving cash in the bank.',
    target: '<30 days is excellent. >45 days means debtors are slow-paying.',
    why: 'Long CCC ties up working capital and increases cash risk. High CCC combined with growth can cause a profitable business to run out of cash.',
  },
  cashRunwayMonths: {
    what: 'Months of cash remaining at the current monthly burn rate.',
    target: '≥3 months is the minimum safe threshold. ≥6 months is comfortable.',
    why: 'The ultimate survival metric. Below 2 months requires immediate action — cost reduction, revenue acceleration, or external financing.',
  },
  revenuePerEmployee: {
    what: 'Total monthly revenue divided by the number of full-time-equivalent staff.',
    target: '≥$25k/FTE/month for a professional services consultancy.',
    why: 'Tracks team-level productivity. A declining trend signals that headcount is growing faster than revenue, or that utilisation is falling.',
  },
};

const DIRECTION_CONFIG: Record<
  TrendInsight['direction'],
  { label: string; className: string }
> = {
  improving: { label: '↑ Improving', className: 'cip-direction--improving' },
  declining: { label: '↓ Declining', className: 'cip-direction--declining' },
  stable: { label: '→ Stable', className: 'cip-direction--stable' },
  volatile: { label: '~ Volatile', className: 'cip-direction--volatile' },
};

const IMPACT_LABELS: Record<string, string> = {
  positive: '▲',
  negative: '▼',
  neutral: '●',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ChartInsightPanelProps {
  metricKey: OlympicBest['metric'];
  metricTitle: string;
  insight: TrendInsight | null;
  loading: boolean;
  generatedAt: string | null;
  events: MonthEvent[];
  onClose: () => void;
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function ChartInsightPanel({
  metricKey,
  metricTitle,
  insight,
  loading,
  generatedAt,
  events,
  onClose,
  onRefresh,
}: ChartInsightPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const def = METRIC_DEFINITIONS[metricKey];

  // Focus trap and scroll lock
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    panelRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const directionCfg = insight
    ? DIRECTION_CONFIG[insight.direction]
    : null;

  return (
    <div
      className="cip-overlay"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="cip-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`AI Insights — ${metricTitle}`}
        ref={panelRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="cip-header">
          <div className="cip-header-left">
            <span className="cip-ai-badge">AI</span>
            <div>
              <h2 className="cip-title">{metricTitle}</h2>
              <p className="cip-subtitle">AI-powered metric analysis</p>
            </div>
          </div>
          <div className="cip-header-actions">
            <button
              className="cip-refresh-btn"
              onClick={onRefresh}
              disabled={loading}
              title="Regenerate insights"
              aria-label="Regenerate insights"
            >
              {loading ? '…' : '↻'}
            </button>
            <button
              className="cip-close-btn"
              onClick={onClose}
              aria-label="Close panel"
            >
              ×
            </button>
          </div>
        </div>

        <div className="cip-body">
          {/* Metric Definition */}
          <section className="cip-section">
            <h3 className="cip-section-title">What is {metricTitle}?</h3>
            <p className="cip-def-what">{def.what}</p>
            <div className="cip-def-row">
              <span className="cip-def-label">Target</span>
              <span className="cip-def-value">{def.target}</span>
            </div>
            <div className="cip-def-row">
              <span className="cip-def-label">Why it matters</span>
              <span className="cip-def-value">{def.why}</span>
            </div>
          </section>

          {/* AI Analysis */}
          <section className="cip-section cip-section--analysis">
            <div className="cip-analysis-header">
              <h3 className="cip-section-title">Current Analysis</h3>
              {directionCfg && (
                <span className={`cip-direction ${directionCfg.className}`}>
                  {directionCfg.label}
                </span>
              )}
            </div>

            {loading && (
              <div className="cip-loading">
                <span className="cip-loading-dot" />
                <span className="cip-loading-dot" />
                <span className="cip-loading-dot" />
                <span className="cip-loading-text">Analysing trends…</span>
              </div>
            )}

            {!loading && !insight && (
              <div className="cip-no-insight">
                <p>No analysis available yet.</p>
                <button className="cip-generate-btn" onClick={onRefresh}>
                  Generate insights
                </button>
              </div>
            )}

            {!loading && insight && (
              <>
                <p className="cip-summary">{insight.summary}</p>
                {insight.keyDrivers.length > 0 && (
                  <div className="cip-drivers">
                    <p className="cip-drivers-label">Key drivers</p>
                    <ul className="cip-drivers-list">
                      {insight.keyDrivers.map((d, i) => (
                        <li key={`${i}-${d.slice(0, 10)}`}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {insight.dataQualityNote && (
                  <p className="cip-quality-note">⚠ {insight.dataQualityNote}</p>
                )}
              </>
            )}
          </section>

          {/* Context factored in */}
          {events.length > 0 && (
            <section className="cip-section">
              <h3 className="cip-section-title">
                Context factored in
                <span className="cip-context-count">{events.length} event{events.length !== 1 ? 's' : ''}</span>
              </h3>
              <ul className="cip-events-list">
                {events.map((ev) => (
                  <li key={ev.id} className="cip-event-item">
                    <span
                      className="cip-event-impact"
                      data-impact={ev.impact}
                      title={ev.impact}
                    >
                      {IMPACT_LABELS[ev.impact] ?? '●'}
                    </span>
                    <span className="cip-event-month">
                      {ev.month}{ev.endMonth ? `–${ev.endMonth}` : ''}
                    </span>
                    <span className="cip-event-desc">{ev.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {events.length === 0 && !loading && (
            <section className="cip-section">
              <p className="cip-no-events">
                No context events recorded. Add events via chart drill-down to enrich future AI analysis.
              </p>
            </section>
          )}

          {/* Footer */}
          {generatedAt && (
            <p className="cip-generated-at">
              Analysis generated {new Date(generatedAt).toLocaleString('en-AU', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
